import type React from "react";
import "./Datagrid.css";
import {
	type ColumnDef,
	type HeaderContext,
	type PaginationState,
	type RowData,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { type ReactNode, useMemo, useEffect, useState, useRef, useCallback } from "react";
import Loader from "../Loader/Loader";
import TablePageControls, { type PageSizeOption, DEFAULT_PAGE_SIZE_OPTIONS } from "./TablePageControls";

export interface SimpleColumn<T> {
	key: keyof T;
	id?: string;
	header: string | ((info: HeaderContext<T, unknown>) => ReactNode);
	/** Plain-text name of the column, for UI outside the table such as a show/hide menu. */
	label?: string;
	cell?: (value: T[keyof T], row: T) => ReactNode;
	hidden?: boolean | (() => boolean);
	/** Footer cell content for totals row */
	footer?: ReactNode;
	/** Column width in pixels */
	width?: number;
	/** Minimum column width in pixels */
	minWidth?: number;
	/**
	 * When autoSizeColumns is on, caps the computed width (e.g. fixed checkbox / icon column).
	 * If omitted, wide tables may assign this column a large share of the width.
	 */
	maxWidth?: number;
	/** Pin column to the left so it stays visible during horizontal scroll. */
	sticky?: boolean;
}

// Re-export from TablePageControls for convenience
export { type PageSizeOption, DEFAULT_PAGE_SIZE_OPTIONS } from "./TablePageControls";

type TableProps<T extends RowData> = {
	noResults: string;
	data: T[];
	simpleColumns: SimpleColumn<T>[];
	pagination: PaginationState;
	setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
	pageCount: number;
	isLoading?: boolean;
	getRowLink?: (rowData: T) => string | undefined;
	rowAction?: (rowData: T, event: React.MouseEvent<HTMLTableRowElement>) => void | undefined;
	columnVisibility?: Record<string, boolean>;
	/** Show a sticky footer row with totals */
	showFooter?: boolean;
	/** Show page size selector */
	showPageSizeSelector?: boolean;
	/** Available page size options (defaults to DEFAULT_PAGE_SIZE_OPTIONS) */
	pageSizeOptions?: PageSizeOption[];
	/** Total number of items (required when "all" option is used) */
	totalItems?: number;
	/** Callback when column widths change */
	onColumnWidthsChange?: (widths: Record<string, number>) => void;
	/** Enable automatic column sizing to fit viewport with manual resize capability (default: false) */
	autoSizeColumns?: boolean;
	/** Enable manual column resizing by dragging column borders (default: false) */
	resizableColumns?: boolean;
	/** Enable sticky column headers that stay fixed when scrolling (default: false) */
	stickyHeader?: boolean;
	/** Wrap only the table in a horizontal scroll container so wide tables scroll without clipping pagination */
	horizontalTableScroll?: boolean;
	/** Data fetch failed; show retry in pagination bar when onRetry is provided */
	isError?: boolean;
	/** Called when user clicks Retry after a failed load */
	onRetry?: () => void;
	/** Shown to the right of the pagination bar when provided (e.g. rating legend from common `FooterInfo`) */
	paginationFooterInfo?: ReactNode;
};

export default function DatagridV2<T extends RowData>({
	noResults,
	data,
	simpleColumns,
	pagination,
	pageCount,
	setPagination,
	isLoading = false,
	getRowLink,
	rowAction,
	columnVisibility,
	showFooter = false,
	showPageSizeSelector = false,
	pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
	totalItems,
	onColumnWidthsChange,
	autoSizeColumns = false,
	resizableColumns = false,
	stickyHeader = false,
	horizontalTableScroll = false,
	isError = false,
	onRetry,
	paginationFooterInfo,
}: TableProps<T>) {
	interface CustomColumnProperties<T> {
		filterOptions?: Array<{ value: string; label: string }>;
	}

	type ExtendedColumnDef<T> = ColumnDef<T> & CustomColumnProperties<T>;

	// Ref for table container to measure available width
	const tableContainerRef = useRef<HTMLTableElement>(null);
	const tableScrollRef = useRef<HTMLDivElement>(null);

	// State for column sizing
	const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});

	const columns: ExtendedColumnDef<T>[] = useMemo(
		() =>
			simpleColumns.map((col) => {
				const colId = col.id || String(col.key);
				// Don't use columnSizing here - TanStack Table handles sizing through state
				// Column definitions should only have initial/default sizes
				return {
					id: colId,
					accessorKey: col.key as keyof T,
					header: typeof col.header === "string" ? col.header : (info) => (col.header as (info: HeaderContext<T, unknown>) => ReactNode)(info),
					cell: col.cell ? (info) => col.cell?.(info.getValue() as T[keyof T], info.row.original) : (info) => info.getValue(),
					size: col.width,
					minSize: col.minWidth,
					...(col.maxWidth !== undefined ? { maxSize: col.maxWidth } : {}),
				};
			}),
		[simpleColumns], // Remove columnSizing from dependencies - TanStack Table handles it via state
	);

	// Calculate and update auto-sized column widths
	const calculateAutoSizing = useCallback(() => {
		if (!autoSizeColumns || !tableContainerRef.current) {
			return; // Don't clear sizing, keep initial values
		}

		const visibleColumns = simpleColumns.filter((col) => {
			const colId = col.id || String(col.key);
			return columnVisibility?.[colId] !== false;
		});

		if (visibleColumns.length === 0) {
			return; // Don't clear sizing
		}

		// Measure the scroll wrapper (or parent) so a wide table min-width does not skew the calculation
		const measureElement =
			tableScrollRef.current ??
			tableContainerRef.current.parentElement ??
			tableContainerRef.current;
		const containerWidth = measureElement.clientWidth || measureElement.offsetWidth;

		// Reserve some space for scrollbar and padding (approximately 20px)
		const availableWidth = Math.max(containerWidth - 20, 100);

		// Calculate total minimum width
		const totalMinWidth = visibleColumns.reduce((sum, col) => {
			return sum + (col.minWidth || 50);
		}, 0);

		// If minimum widths exceed available width, use minimum widths
		if (totalMinWidth >= availableWidth) {
			const sizing: Record<string, number> = {};
			visibleColumns.forEach((col) => {
				const colId = col.id || String(col.key);
				sizing[colId] = col.minWidth || 50;
			});
			// Only update if sizes actually changed to prevent unnecessary re-renders
			setColumnSizing((prev) => {
				const hasChanges = Object.keys(sizing).some(
					(key) => prev[key] !== sizing[key]
				) || Object.keys(prev).some((key) => !sizing[key]);
				return hasChanges ? sizing : prev;
			});
			return;
		}

		// Distribute available width equally
		const equalWidth = Math.floor(availableWidth / visibleColumns.length);
		const sizing: Record<string, number> = {};

		visibleColumns.forEach((col) => {
			const colId = col.id || String(col.key);
			const minWidth = col.minWidth || 50;
			sizing[colId] = Math.max(equalWidth, minWidth);
		});

		let totalAssigned = Object.values(sizing).reduce((sum, width) => sum + width, 0);
		if (totalAssigned > availableWidth) {
			visibleColumns.forEach((col) => {
				const colId = col.id || String(col.key);
				sizing[colId] = col.minWidth || 50;
			});
		} else if (totalAssigned < availableWidth) {
			let remaining = availableWidth - totalAssigned;
			const growableColumns = visibleColumns.filter((col) => {
				const colId = col.id || String(col.key);
				const maxWidth = col.maxWidth;
				return maxWidth === undefined || sizing[colId] < maxWidth;
			});

			while (remaining > 0 && growableColumns.length > 0) {
				const share = Math.max(1, Math.floor(remaining / growableColumns.length));
				let distributed = 0;
				for (const col of growableColumns) {
					const colId = col.id || String(col.key);
					const maxWidth = col.maxWidth ?? Number.POSITIVE_INFINITY;
					const headroom = maxWidth - sizing[colId];
					if (headroom <= 0) {
						continue;
					}
					const add = Math.min(share, headroom, remaining);
					sizing[colId] += add;
					distributed += add;
					remaining -= add;
				}
				if (distributed === 0) {
					break;
				}
			}
		}

		// Only update if sizes actually changed to prevent unnecessary re-renders
		setColumnSizing((prev) => {
			const hasChanges = Object.keys(sizing).some(
				(key) => prev[key] !== sizing[key]
			) || Object.keys(prev).some((key) => !sizing[key]);
			return hasChanges ? sizing : prev;
		});
	}, [simpleColumns, columnVisibility, autoSizeColumns]);

	// Track if user has manually resized columns
	const [hasManualResize, setHasManualResize] = useState(false);

	// Reset manual resize flag when column visibility changes (so auto-sizing can recalculate)
	useEffect(() => {
		if (autoSizeColumns) {
			setHasManualResize(false);
		}
	}, [columnVisibility, autoSizeColumns]);

	// Calculate sizing on mount and when dependencies change
	// Only auto-calculate if user hasn't manually resized columns
	useEffect(() => {
		if (autoSizeColumns && !hasManualResize) {
			// Use requestAnimationFrame to ensure table is rendered and avoid unnecessary recalculations
			const rafId = requestAnimationFrame(() => {
				calculateAutoSizing();
			});
			return () => cancelAnimationFrame(rafId);
		}
	}, [calculateAutoSizing, autoSizeColumns, hasManualResize, columnVisibility]);

	// Handle window resize for auto-sizing (only if user hasn't manually resized)
	useEffect(() => {
		if (!autoSizeColumns) return;

		let resizeTimer: NodeJS.Timeout;
		const handleResize = () => {
			// Debounce resize events
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				// Only auto-resize if user hasn't manually adjusted columns
				if (!hasManualResize) {
					calculateAutoSizing();
				}
			}, 150);
		};

		window.addEventListener('resize', handleResize);

		const observedElement = tableScrollRef.current ?? tableContainerRef.current?.parentElement ?? tableContainerRef.current;
		let resizeObserver: ResizeObserver | undefined;
		if (observedElement !== null && observedElement !== undefined) {
			resizeObserver = new ResizeObserver(() => {
				clearTimeout(resizeTimer);
				resizeTimer = setTimeout(() => {
					if (hasManualResize === false) {
						calculateAutoSizing();
					}
				}, 150);
			});
			resizeObserver.observe(observedElement);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			clearTimeout(resizeTimer);
			resizeObserver?.disconnect();
		};
	}, [calculateAutoSizing, autoSizeColumns, hasManualResize]);

	const table = useReactTable({
		data: data,
		pageCount: pageCount,
		state: {
			pagination,
			columnVisibility,
			...((autoSizeColumns || resizableColumns) && { columnSizing }),
		},
		onPaginationChange: setPagination,
		...(resizableColumns && {
			onColumnSizingChange: (updater) => {
				setColumnSizing(updater);
				// Mark that user has manually resized
				setHasManualResize(true);
			}
		}),
		columns,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		enableColumnResizing: resizableColumns, // Enable manual resizing based on resizableColumns prop
		...(resizableColumns && {
			columnResizeMode: "onChange",
			defaultColumn: {
				minSize: 50,
				size: 150,
			},
		}),
	});

	// Sync column widths back to parent when they change (only if resizableColumns or autoSizeColumns is enabled)
	useEffect(() => {
		if ((resizableColumns || autoSizeColumns) && onColumnWidthsChange && Object.keys(columnSizing).length > 0) {
			const widths: Record<string, number> = {};
			table.getAllColumns().forEach((column) => {
				if (column.id) {
					widths[column.id] = column.getSize();
				}
			});
			onColumnWidthsChange(widths);
		}
	}, [columnSizing, onColumnWidthsChange, table, resizableColumns, autoSizeColumns]);

	// Honor explicit per-column width / maxWidth even when autoSizeColumns is off so
	// SimpleColumn's min/max constraints are respected. `max-width` on table cells only
	// works reliably with `table-layout: fixed`, so we flip the layout when any column
	// has an explicit width or maxWidth defined.
	const hasExplicitColumnWidths = useMemo(
		() => simpleColumns.some((col) => col.width !== undefined || col.maxWidth !== undefined),
		[simpleColumns],
	);

	const shouldUseFixedTableLayout = autoSizeColumns || hasExplicitColumnWidths;

	const resolveAutoSizedColumnWidth = useCallback(
		(columnId: string): number | undefined => {
			const simpleCol = simpleColumns.find((sc) => (sc.id || String(sc.key)) === columnId);
			if (!simpleCol) {
				return undefined;
			}

			const sized = columnSizing[columnId];
			if (sized === undefined) {
				if (simpleCol.width !== undefined) {
					return simpleCol.width;
				}
				if (simpleCol.minWidth !== undefined) {
					return simpleCol.minWidth;
				}
				return undefined;
			}

			let width = sized;
			if (simpleCol.maxWidth !== undefined) {
				width = Math.min(width, simpleCol.maxWidth);
			}
			if (simpleCol.minWidth !== undefined) {
				width = Math.max(width, simpleCol.minWidth);
			}
			return width;
		},
		[columnSizing, simpleColumns],
	);

	const stickyColumns = useMemo(() => {
		const map = new Map<string, number>();
		let offset = 0;
		for (const col of simpleColumns) {
			const colId = col.id || String(col.key);
			if (columnVisibility?.[colId] === false) {
				continue;
			}
			if (col.sticky) {
				map.set(colId, offset);
				const width = autoSizeColumns
					? resolveAutoSizedColumnWidth(colId) ?? col.width ?? col.minWidth ?? 56
					: col.width ?? col.minWidth ?? 56;
				offset += width;
			}
		}
		return map;
	}, [simpleColumns, columnVisibility, autoSizeColumns, resolveAutoSizedColumnWidth]);

	const getColumnWidthStyle = useCallback(
		(columnId: string): React.CSSProperties | undefined => {
			const simpleCol = simpleColumns.find((sc) => (sc.id || String(sc.key)) === columnId);
			if (!simpleCol) {
				return undefined;
			}

			if (autoSizeColumns) {
				const width = resolveAutoSizedColumnWidth(columnId);
				if (width === undefined) {
					return { width: "auto" };
				}
				return { width: `${width}px` };
			}

			const style: React.CSSProperties = {};
			if (simpleCol.width !== undefined) {
				style.width = simpleCol.width;
			}
			if (simpleCol.minWidth !== undefined) {
				style.minWidth = simpleCol.minWidth;
			}
			if (simpleCol.maxWidth !== undefined) {
				style.maxWidth = simpleCol.maxWidth;
			}
			if (Object.keys(style).length === 0) {
				return undefined;
			}
			return style;
		},
		[autoSizeColumns, resolveAutoSizedColumnWidth, simpleColumns],
	);

	const assignedTableWidth = useMemo(() => {
		if (!autoSizeColumns) {
			return 0;
		}

		return simpleColumns.reduce((sum, col) => {
			const colId = col.id || String(col.key);
			if (columnVisibility?.[colId] === false) {
				return sum;
			}
			return sum + (resolveAutoSizedColumnWidth(colId) ?? col.minWidth ?? 50);
		}, 0);
	}, [autoSizeColumns, columnSizing, columnVisibility, resolveAutoSizedColumnWidth, simpleColumns]);

	const tableWidthStyle = useMemo((): React.CSSProperties => {
		if (horizontalTableScroll) {
			if (shouldUseFixedTableLayout && assignedTableWidth > 0) {
				return { width: "100%", minWidth: assignedTableWidth };
			}
			return { width: "max-content", minWidth: "100%" };
		}
		return { width: "100%" };
	}, [horizontalTableScroll, shouldUseFixedTableLayout, assignedTableWidth]);

	const tableInner = (
		<table
			ref={tableContainerRef}
			className={`datagrifd2Table ${autoSizeColumns ? "datagrifd2Table--resizable" : ""} ${stickyHeader ? "datagrifd2Table--stickyHeader" : ""}`}
			style={{ ...(shouldUseFixedTableLayout && { tableLayout: "fixed" as const }), ...tableWidthStyle }}
		>
			<thead className="datagrifd2Table-header">
				{table.getHeaderGroups().map((headerGroup) => (
					<tr key={headerGroup.id}>
						{headerGroup.headers.map((header) => {
							const stickyLeft = stickyColumns.get(header.id);
							const isSticky = stickyLeft !== undefined;
							return (
								<th
									className={`datagrifd2Table-headerCell${isSticky ? " datagrifd2Table-headerCell--sticky" : ""}`}
									key={header.id}
									style={{
										...getColumnWidthStyle(header.id),
										...(autoSizeColumns && {
											position: isSticky ? "sticky" : "relative",
										}),
										...(isSticky && { position: "sticky", left: stickyLeft, zIndex: 3 }),
									}}
								>
									{flexRender(header.column.columnDef.header, header.getContext())}
									{resizableColumns && header.column.getCanResize() && (
										<div
											onMouseDown={header.getResizeHandler()}
											onTouchStart={header.getResizeHandler()}
											className={`datagrifd2Table-resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
										/>
									)}
								</th>
							);
						})}
					</tr>
				))}
			</thead>
			{isLoading ? (
				<tbody className="datagrifd2Table-body">
					<tr>
						<td colSpan={table.getVisibleFlatColumns().length}>
							<Loader />
						</td>
					</tr>
				</tbody>
			) : (
				<tbody className="datagrifd2Table-body">
					{table.getRowModel().rows.map((row) => {
						const rowData = row.original as T;
						const link = getRowLink && !rowAction ? getRowLink(rowData) : undefined;

						return (
							<tr
								className={`datagrifd2Table-row ${link || rowAction ? "datagrifd2Table-row--hover" : ""}`}
								key={row.id}
								onClick={rowAction ? (event) => rowAction(rowData, event) : undefined}
							>
								{row.getVisibleCells().map((cell) => {
									const stickyLeft = stickyColumns.get(cell.column.id);
									const isSticky = stickyLeft !== undefined;
									return (
										<td
											key={cell.id}
											className={`datagrifd2Table-cell${isSticky ? " datagrifd2Table-cell--sticky" : ""}`}
											style={{
												...getColumnWidthStyle(cell.column.id),
												...(isSticky && { position: "sticky", left: stickyLeft, zIndex: 1 }),
											}}
										>
											{link ? (
												<a href={link} style={{ display: 'block', width: '100%', height: '100%' }}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</a>
											) : (
												flexRender(cell.column.columnDef.cell, cell.getContext())
											)}

										</td>
									);
								})}
							</tr>
						)
					})}
				</tbody>
			)}
			{showFooter && !isLoading && data.length > 0 && (
				<tfoot className="datagrifd2Table-footer">
					<tr className="datagrifd2Table-footerRow">
						{table.getVisibleFlatColumns().map((column) => {
							const simpleCol = simpleColumns.find(
								(sc) => (sc.id || String(sc.key)) === column.id
							);
							return (
								<td
									key={column.id}
									className="datagrifd2Table-footerCell"
									style={getColumnWidthStyle(column.id)}
								>
									{simpleCol?.footer ?? ""}
								</td>
							);
						})}
					</tr>
				</tfoot>
			)}
		</table>
	);

	return (
		<>
			{horizontalTableScroll ? <div ref={tableScrollRef} className="datagrifd2Table-scroll">{tableInner}</div> : tableInner}
			{!isLoading && data.length === 0 && <div className="datagrifd2Table-noData">{noResults}</div>}
			<TablePageControls
				table={table}
				showPageSizeSelector={showPageSizeSelector}
				pageSizeOptions={pageSizeOptions}
				totalItems={totalItems}
				setPagination={setPagination}
				isError={isError}
				onRetry={onRetry}
				paginationFooterInfo={paginationFooterInfo}
			/>
		</>
	);
}
